<svelte:options
  customElement={{
    tag: "oc-tooltip-v1",
    /*                                            */
    extend: window.__components.extend(),
    props: {
      position: { type: "String" },
    },
  }}
/>

<script lang="ts">
  /*                                            */
  import { on } from "svelte/events";
  /*                                            */
  import { MediaQuery } from "svelte/reactivity";

  import { onMount, tick } from "svelte";
  import { useTooltipPosition } from "../../../common/utils/useTooltipPosition";
  import { longPressEvent } from "../../../common/actions/longPressEvent";
  import type { Props } from "./TooltipV1.types";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  const isHoverable = new MediaQuery("not all and (hover: none)");

  let { position = "bottom", disabled = false }: Props = $props();

  let body = $state<HTMLElement>();
  let root = $state<HTMLElement>();

  const pos = useTooltipPosition();
  let tooltipOpen = $state(false);
  let ready = $state(false);

  function open(): void {
    if (disabled) return;

    tooltipOpen = true;

    /*                                                                                      */
    setTimeout(() => pos.reposition(body!, position));
  }

  const handleOpen = (): unknown => isHoverable.current && open();

  const close = (): unknown => (tooltipOpen = false);

  onMount(() => {
    tick().then(() => {
      ready = true;
    });

    /*                                                     */
    const unsub = on(
      document,
      "pointerdown",
      (e) => {
        if (!tooltipOpen) return;
        const target = e.target as Node | null;

        /*                                                                           */
        if (root && target && root.contains(target)) return;
        close();
      },
      { capture: true, passive: true },
    );

    return () => {
      unsub();
      ready = false;
    };
  });

  $effect(() => {
    pos.reposition(body!, position);
  });
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<!-- eslint-disable-next-line svelte/valid-compile -->
<!-- svelte-ignore a11y_mouse_events_have_key_events -->
<!-- eslint-disable svelte/no-at-html-tags -->
<div
  class="tooltip-box"
  data-position-v={$pos.vertical}
  data-position-h={$pos.horizontal}
  onmouseover={handleOpen}
  onmouseleave={close}
  onfocusin={handleOpen}
  onfocusout={close}
  use:longPressEvent={500}
  onlongpress={open}
  onkeydown={({ code }) => {
    if (tooltipOpen && code === "Escape") {
      close();
    }
  }}
>
  <slot />

  <div
    class="tooltip-anchor"
    class:visible={tooltipOpen}
    class:ready
    class:positioning={$pos.positioning}
  >
    <span class="arrow"></span>
    <div class="body" bind:this={body}>
      <slot name="tooltip-content" />
    </div>
  </div>
</div>

<DefaultStyles />

<style lang="scss" global>
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";

  :host {
    display: block;
    /*                                                                                                                                 */
    width: fit-content;
  }

  $verticalOffset: calc(
    tokens.$oc-component-tooltip-context-spacing + tokens.$oc-component-tooltip-arrow-height
  );

  .tooltip-box {
    /*                                                                                                                                 */
    display: flex;
    flex-direction: column;
    align-items: center;

    .body {
      filter: tokens.$oc-semantic-shadow-above;
      transform: translateZ(0);
    }

    &[data-position-v="bottom"] {
      flex-direction: column;

      .tooltip-anchor {
        justify-content: flex-end;
      }

      .arrow {
        transform: translateY($verticalOffset);
      }

      .body {
        transform: translateY(calc(100% + $verticalOffset)) translateZ(0);
      }
    }

    &[data-position-v="top"] {
      flex-direction: column-reverse;

      .tooltip-anchor {
        justify-content: flex-start;
      }

      .arrow {
        transform: rotate(180deg) translateY($verticalOffset);
      }

      .body {
        transform: translateY(calc(-100% - $verticalOffset)) translateZ(0);
      }
    }

    &[data-position-h="left"] {
      .body {
        left: tokens.$oc-component-tooltip-frame-spacing;
      }
    }

    &[data-position-h="right"] {
      .body {
        right: tokens.$oc-component-tooltip-frame-spacing;
      }
    }
  }

  .tooltip-anchor {
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 9999;
    opacity: 0;
    visibility: hidden;

    &.ready {
      transition:
        opacity tokens.$oc-component-tooltip-show-hide-transition-duration
          tokens.$oc-component-tooltip-show-hide-transition-easing
          tokens.$oc-component-tooltip-hide-delay,
        visibility 0ms tokens.$oc-component-tooltip-show-hide-transition-duration +
          tokens.$oc-component-tooltip-hide-delay;
    }

    &.visible {
      visibility: visible;
      opacity: 1;
      transition: opacity tokens.$oc-component-tooltip-show-hide-transition-duration
        tokens.$oc-component-tooltip-show-hide-transition-easing
        tokens.$oc-component-tooltip-show-delay;
    }

    &.positioning {
      opacity: 0;
      /*                                                                                                                       */
      overflow: hidden;
      /*                                                                                                                               */
      position: relative;
    }
  }

  .arrow {
    $seamFixHeight: 1px;

    width: calc(tokens.$oc-component-tooltip-arrow-width + $seamFixHeight * 2);
    height: calc(tokens.$oc-component-tooltip-arrow-height + $seamFixHeight);
    background-color: tokens.$oc-component-tooltip-background-color;
    margin-bottom: -$seamFixHeight;
    clip-path: polygon(0% 100%, 50% 0%, 100% 100%);
    transform-origin: center;
    position: absolute;
    z-index: 9999;
  }

  .body {
    position: absolute;

    max-width: min(
      tokens.$oc-component-tooltip-max-width,
      calc(100vw - 2 * tokens.$oc-component-tooltip-frame-spacing)
    );
    width: max-content;
    background-color: tokens.$oc-component-tooltip-background-color;
    color: tokens.$oc-component-tooltip-copy-color;
    border-radius: tokens.$oc-component-tooltip-border-radius;
    padding: tokens.$oc-component-tooltip-spacing-y tokens.$oc-component-tooltip-spacing-x;
    font: tokens.$oc-component-tooltip-copy-font;
    z-index: 9999;
  }
</style>
